import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './AdminUsers.css'
import LoadingScreen from '../components/LoadingScreen'
import ConfirmModal from '../components/ConfirmModal'

const AdminUsers = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const history = useHistory()

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Ensure rejection_status is included for all users
        const usersWithStatus = (data.users || []).map(user => ({
          ...user,
          rejection_status: user.rejection_status || null,
          is_active: user.is_active !== undefined ? user.is_active : true
        }))
        setUsers(usersWithStatus)
        setError('')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.error || 'Failed to load users')
        toast.error(errorData.error || 'Failed to load users', {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (err) {
      setError('Network error')
      toast.error('Network error. Please check your connection.', {
        position: "top-right",
        autoClose: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleChangeRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      })

      if (response.ok) {
        toast.success('Role updated successfully!', {
          position: "top-right",
          autoClose: 2000,
        })
        setUsers(users.map(user =>
          user.id === userId ? { ...user, role: newRole } : user
        ))
      } else {
        toast.error('Failed to update role', {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (err) {
      toast.error('Network error. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  const confirmDeleteUser = (user) => {
    setUserToDelete(user)
    setDeleteModalOpen(true)
  }

  const handleDeleteUser = async () => {
    if (!userToDelete) return
    const userId = userToDelete.id

    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        toast.success('User deleted successfully!', {
          position: "top-right",
          autoClose: 2000,
        })
        setUsers(users.filter(user => user.id !== userId))
      } else {
        toast.error('Failed to delete user', {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (err) {
      toast.error('Network error. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  const getFilteredUsers = () => {
    let filtered = users

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter)
    }

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'role-admin',
      teacher: 'role-teacher',
      invigilator: 'role-invigilator',
      student: 'role-student',
      volunteer: 'role-volunteer'
    }
    return classes[role] || 'role-default'
  }

  const handleApproveUser = async (userId, userRole) => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const roleName = userRole.charAt(0).toUpperCase() + userRole.slice(1)
        toast.success(`${roleName} approved successfully!`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
        // Refresh users list after a short delay to ensure backend has updated
        setTimeout(() => {
          fetchUsers()
        }, 500)
      } else {
        const data = await response.json()
        toast.error(data.error || 'Failed to approve user', {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (err) {
      toast.error('Network error. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  const handleRejectUser = async (userId, userRole) => {
    const roleName = userRole.charAt(0).toUpperCase() + userRole.slice(1)
    
    // Use a more elegant confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to reject this ${roleName.toLowerCase()}?`)
    if (!confirmed) return
    
    const rejectionReason = prompt(`Please provide a reason for rejection (optional):`)
    
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/admin/users/${userId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejection_reason: rejectionReason || `Your ${roleName.toLowerCase()} application has been rejected.`
        })
      })

      if (response.ok) {
        toast.success(`${roleName} rejected successfully.`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
        // Refresh users list after a short delay to ensure backend has updated
        setTimeout(() => {
          fetchUsers()
        }, 500)
      } else {
        const data = await response.json()
        toast.error(data.error || 'Failed to reject user', {
          position: "top-right",
          autoClose: 3000,
        })
      }
    } catch (err) {
      toast.error('Network error. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  // Legacy functions for backward compatibility
  const handleApproveVolunteer = (userId) => handleApproveUser(userId, 'volunteer')
  const handleRejectVolunteer = (userId) => handleRejectUser(userId, 'volunteer')

  if (loading) {
    return <LoadingScreen message="Loading Users" />
  }

  const filteredUsers = getFilteredUsers()

  return (
    <div className="admin-users-container">
      <ToastContainer />
      <header className="page-header">
        <div>
          <h1>User Management</h1>
          <p className="subtitle">Manage all system users and their roles</p>
        </div>
        <button onClick={() => history.push('/admin-dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="users-stats">
        <div className="stat-card">
          <span className="stat-number">{users.length}</span>
          <span className="stat-label">Total Users</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'student').length}</span>
          <span className="stat-label">Students</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'teacher').length}</span>
          <span className="stat-label">Teachers</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'invigilator').length}</span>
          <span className="stat-label">Invigilators</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{users.filter(u => u.role === 'volunteer').length}</span>
          <span className="stat-label">Volunteers</span>
        </div>
      </div>

      <div className="admin-users-content">
        <div className="controls-section">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <div className="filter-buttons">
            <button
              className={`filter-btn ${roleFilter === 'all' ? 'active' : ''}`}
              onClick={() => setRoleFilter('all')}
            >
              All Roles
            </button>
            <button
              className={`filter-btn ${roleFilter === 'student' ? 'active' : ''}`}
              onClick={() => setRoleFilter('student')}
            >
              Students
            </button>
            <button
              className={`filter-btn ${roleFilter === 'teacher' ? 'active' : ''}`}
              onClick={() => setRoleFilter('teacher')}
            >
              Teachers
            </button>
            <button
              className={`filter-btn ${roleFilter === 'invigilator' ? 'active' : ''}`}
              onClick={() => setRoleFilter('invigilator')}
            >
              Invigilators
            </button>
            <button
              className={`filter-btn ${roleFilter === 'volunteer' ? 'active' : ''}`}
              onClick={() => setRoleFilter('volunteer')}
            >
              Volunteers
            </button>
          </div>
        </div>

        <div className="users-table-container">
          {filteredUsers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>No Users Found</h3>
              <p>No users match your search criteria</p>
            </div>
          ) : (
            <table className="users-table rts-stack-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td data-label="Name" className="user-name">{user.full_name}</td>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Role">
                      <select
                        value={user.role}
                        onChange={(e) => handleChangeRole(user.id, e.target.value)}
                        className={`role-select ${getRoleBadgeClass(user.role)}`}
                      >
                        <option value="student">Student</option>
                        <option value="teacher">Teacher</option>
                        <option value="invigilator">Invigilator</option>
                        <option value="volunteer">Volunteer</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td data-label="Joined">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        {['volunteer', 'teacher', 'invigilator'].includes(user.role) && 
                         !user.is_active && 
                         user.rejection_status === 'pending' && (
                          <>
                            <button
                              className="approve-btn"
                              onClick={() => handleApproveUser(user.id, user.role)}
                            >
                              Approve
                            </button>
                            <button
                              className="reject-btn"
                              onClick={() => handleRejectUser(user.id, user.role)}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {['volunteer', 'teacher', 'invigilator'].includes(user.role) && 
                         !user.is_active && 
                         user.rejection_status === 'pending' && (
                          <span className="pending-badge">Pending Approval</span>
                        )}
                        {['volunteer', 'teacher', 'invigilator'].includes(user.role) && 
                         !user.is_active && 
                         user.rejection_status === 'rejected' && (
                          <span className="rejected-badge">Rejected</span>
                        )}
                        <button
                          className="delete-btn"
                          onClick={() => confirmDeleteUser(user)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false)
          setUserToDelete(null)
        }}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete "${userToDelete?.full_name}"? This action cannot be undone.`}
        confirmText="Delete"
        isDangerous={true}
      />
    </div>
  )
}

export default AdminUsers
